import { useMemo } from 'react';
import { Label, Input } from '@/components/FormElements';
import { decodeColors } from '@/utils/colors';
import TrendlineFields from '@/components/Editor/Form/fields/Trendline';
import DataLabels from '@/components/Editor/Form/fields/DataLabels';
import ColorPicker from '@/components/Editor/Form/fields/common/ColorPicker';
import PointStyles, { checkPointStylesModified } from '@/components/Editor/Form/fields/PointStyles';
import { TemplateSection } from '@/components/Editor/Form/fields/Template';
import parentStyles from '@/features/WidgetEdit/Form/styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_LABELS = {
  label: {},
  trendline: {}
};

const WidgetEditFormFieldsCollectionOption = _ref => {
  var _config$dataLabels, _config$dataLabels2, _config$dataLabels3, _config$dataLabels4, _config$dataLabels5, _config$dataLabels6, _config$trendline, _config$trendline2, _config$trendline3, _config$trendline4, _config$trendline5, _config$trendline6;

  let {
    namePrefix,
    index,
    config,
    exampleValue = 0.01,
    labels: srcLabels = {},
    defaultValues = {},
    hasLabel = true,
    singleLabel = false,
    colored,
    colorPickerLabel = 'Color',
    hasTrendline,
    hasDataLabels,
    hasPointStyles,
    hasPointRadius,
    hasTemplate,
    disabledPointStyles,
    children,
    onChange
  } = _ref;
  const labelFieldId = `${namePrefix}-label-${index}`;
  const colorPickerFieldId = `${namePrefix}-color-picker-${index}`;
  const pointStylesFieldId = `${namePrefix}-pointStyles-${index}`;
  const templateFieldId = `${namePrefix}-template-${index}`;

  if (!srcLabels || typeof srcLabels !== 'object' || Array.isArray(srcLabels)) {
    throw TypeError('labels must be an object');
  } // Setup labels with defaults


  const labels = Object.assign({}, DEFAULT_LABELS, srcLabels);

  if (srcLabels.typographic) {
    Object.assign(labels.typographic, srcLabels.typographic);
  } // Check if point styles have been modified


  const isPointStylesModified = useMemo(() => checkPointStylesModified(config || {}), [config]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [singleLabel && hasLabel ? /*#__PURE__*/_jsx(Label, {
      forInput: labelFieldId,
      className: parentStyles.formField,
      text: labels.label.text || 'Result label',
      caption: labels.label.caption || 'User facing label for group.',
      children: /*#__PURE__*/_jsx("div", {
        className: parentStyles.formField__row,
        children: /*#__PURE__*/_jsx(Input, {
          id: labelFieldId,
          placeholder: labels.label.placeholder || 'Custom label',
          defaultValue: config.label || defaultValues.label || '',
          onChange: evt => onChange({
            label: evt.target.value
          })
        })
      })
    }) : /*#__PURE__*/_jsx(_Fragment, {}), colored ? /*#__PURE__*/_jsx(ColorPicker, {
      label: colorPickerLabel,
      caption: "Customize result color",
      id: colorPickerFieldId,
      defaultValue: decodeColors(config.color).shift() || defaultValues.color,
      onChange: color => onChange({
        color: typeof color === 'string' ? color : null
      })
    }, colorPickerFieldId) : /*#__PURE__*/_jsx(_Fragment, {}), hasTemplate ? /*#__PURE__*/_jsx(TemplateSection, {
      id: templateFieldId,
      label: "Formatting",
      caption: "How data labels will be presented",
      exampleValue: exampleValue,
      template: (config === null || config === void 0 ? void 0 : config.template) || (defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.template),
      onChange: value => onChange({
        template: value
      })
    }) : /*#__PURE__*/_jsx(_Fragment, {}), hasDataLabels ? /*#__PURE__*/_jsx(DataLabels, {
      nameSpace: namePrefix,
      exampleValue: exampleValue,
      display: (config === null || config === void 0 ? void 0 : (_config$dataLabels = config.dataLabels) === null || _config$dataLabels === void 0 ? void 0 : _config$dataLabels.display) || false,
      anchoring: config === null || config === void 0 ? void 0 : (_config$dataLabels2 = config.dataLabels) === null || _config$dataLabels2 === void 0 ? void 0 : _config$dataLabels2.anchoring,
      alignment: config === null || config === void 0 ? void 0 : (_config$dataLabels3 = config.dataLabels) === null || _config$dataLabels3 === void 0 ? void 0 : _config$dataLabels3.alignment,
      rotation: config === null || config === void 0 ? void 0 : (_config$dataLabels4 = config.dataLabels) === null || _config$dataLabels4 === void 0 ? void 0 : _config$dataLabels4.rotation,
      template: (config === null || config === void 0 ? void 0 : (_config$dataLabels5 = config.dataLabels) === null || _config$dataLabels5 === void 0 ? void 0 : _config$dataLabels5.template) || (defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.template),
      color: config === null || config === void 0 ? void 0 : (_config$dataLabels6 = config.dataLabels) === null || _config$dataLabels6 === void 0 ? void 0 : _config$dataLabels6.color,
      onChange: update => onChange(update, 'dataLabels')
    }) : /*#__PURE__*/_jsx(_Fragment, {}), hasTrendline ? /*#__PURE__*/_jsx(TrendlineFields, {
      description: "Regression line for all selections' data points.",
      isCreated: Boolean(config.trendline),
      nameSpace: namePrefix,
      display: (config === null || config === void 0 ? void 0 : (_config$trendline = config.trendline) === null || _config$trendline === void 0 ? void 0 : _config$trendline.display) || false,
      borderWidth: config === null || config === void 0 ? void 0 : (_config$trendline2 = config.trendline) === null || _config$trendline2 === void 0 ? void 0 : _config$trendline2.borderWidth,
      borderDash: config === null || config === void 0 ? void 0 : (_config$trendline3 = config.trendline) === null || _config$trendline3 === void 0 ? void 0 : _config$trendline3.borderDash,
      borderColor: (config === null || config === void 0 ? void 0 : (_config$trendline4 = config.trendline) === null || _config$trendline4 === void 0 ? void 0 : _config$trendline4.borderColor) || (defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.trendlineBorderColor),
      backgroundColor: config === null || config === void 0 ? void 0 : (_config$trendline5 = config.trendline) === null || _config$trendline5 === void 0 ? void 0 : _config$trendline5.backgroundColor,
      tension: config === null || config === void 0 ? void 0 : (_config$trendline6 = config.trendline) === null || _config$trendline6 === void 0 ? void 0 : _config$trendline6.tension,
      textOverrides: {
        borderFieldsLegend: labels.trendline.borderFieldsLegend,
        clearBorder: labels.trendline.clearBorder,
        colorPickerLabel: labels.trendline.colorPickerLabel
      },
      onChange: update => onChange(update, 'trendline'),
      onDelete: () => onChange({
        trendline: null
      })
    }) : /*#__PURE__*/_jsx(_Fragment, {}), hasPointStyles ? /*#__PURE__*/_jsx(PointStyles, {
      nameSpace: pointStylesFieldId,
      variant: isPointStylesModified ? 'active' : '',
      pointRadius: config === null || config === void 0 ? void 0 : config.pointRadius,
      pointStyle: config === null || config === void 0 ? void 0 : config.pointStyle,
      pointBackgroundColor: config === null || config === void 0 ? void 0 : config.pointBackgroundColor,
      disabledPointStyles: disabledPointStyles,
      hasPointRadius: hasPointRadius,
      onChange: update => onChange(update)
    }, pointStylesFieldId) : /*#__PURE__*/_jsx(_Fragment, {}), children ? children : /*#__PURE__*/_jsx(_Fragment, {})]
  });
};

export default WidgetEditFormFieldsCollectionOption;